import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { DsIconButtonV2Component } from '../buttons/ds-icon-button-v2.component';
import { DsIconComponent } from '../icons/ds-icon.component';
import type { DsIconName } from '../icons/ds-icons';
import { DsScrollComponent } from '../styles/ds-scroll';
import {
  DsTagPillComponent,
  type DsTagPillVariant,
} from '../tags/ds-tag-pill.component';

export interface DsFloatingMenuTag {
  label: string;
  variant?: DsTagPillVariant;
  invert?: boolean;
}

export interface DsFloatingMenuItem {
  label: string;
  value: string;
  icon: DsIconName | string;
  disabled?: boolean;
  ariaLabel?: string;
  opensMoreMenu?: boolean;
  tag?: DsFloatingMenuTag;
}

export interface DsFloatingMenuOption {
  label: string;
  value: string;
  icon: DsIconName | string;
  disabled?: boolean;
  ariaLabel?: string;
}

@Component({
  selector: 'ds-floating-menu',
  standalone: true,
  imports: [
    DsIconComponent,
    DsIconButtonV2Component,
    DsScrollComponent,
    DsTagPillComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (moreOpen && moreItems.length > 0) {
      <div
        class="ds-floating-menu__overlay"
        (click)="onBackdropClick()"
      >
        <section
          class="ds-floating-menu__dialog"
          role="dialog"
          aria-modal="true"
          [attr.aria-labelledby]="moreDialogTitleId"
          (click)="$event.stopPropagation()"
          (keydown)="onDialogKeydown($event)"
        >
          <header class="ds-floating-menu__dialog-header">
            <h2
              class="ds-floating-menu__dialog-title ds-text--visual-padding-none"
              [id]="moreDialogTitleId"
            >
              {{ moreTitle }}
            </h2>
            <ds-icon-button-v2
              data-ds-floating-menu-close
              icon="Close"
              emphasis="secondary"
              size="s"
              [invert]="true"
              [ariaLabel]="closeAriaLabel"
              (clicked)="closeMoreMenu()"
            />
          </header>

          <div dsScroll="inverse" class="ds-floating-menu__dialog-body">
            @for (option of moreItems; track option.value; let optionIndex = $index) {
              <button
                class="ds-floating-menu__option"
                [class.ds-floating-menu__option--selected]="isMoreOptionSelected(option)"
                type="button"
                [disabled]="disabled || option.disabled || null"
                [attr.aria-current]="isMoreOptionSelected(option) ? 'page' : null"
                [attr.aria-label]="option.ariaLabel || null"
                (click)="selectMoreOption(option)"
                (keydown)="onMoreOptionKeydown($event, optionIndex)"
              >
                <ds-icon
                  aria-hidden="true"
                  [name]="option.icon"
                  [size]="'var(--ds-floating-menu-more-option-icon-size)'"
                />
                <span class="ds-floating-menu__option-label ds-text--visual-padding-none">
                  {{ option.label }}
                </span>
              </button>
            }
          </div>
        </section>
      </div>
    }

    @if (items.length > 0) {
      <nav
        class="ds-floating-menu"
        [class.ds-floating-menu--more-open]="moreOpen && moreItems.length > 0"
        [attr.aria-label]="ariaLabel"
        [attr.aria-disabled]="disabled || null"
      >
        @for (item of items; track item.value; let index = $index) {
          <button
            class="ds-floating-menu__item"
            type="button"
            [class.ds-floating-menu__item--selected]="index === selectedIndex && !disabled && !item.disabled"
            [disabled]="disabled || item.disabled || null"
            [attr.aria-current]="index === selectedIndex && !disabled && !item.disabled ? 'page' : null"
            [attr.aria-label]="item.ariaLabel || null"
            [attr.aria-haspopup]="item.opensMoreMenu && moreItems.length > 0 ? 'dialog' : null"
            [attr.aria-expanded]="item.opensMoreMenu && moreItems.length > 0 ? moreOpen : null"
            (click)="selectAt(index, true, $event.currentTarget)"
            (keydown)="onKeydown($event, index)"
          >
            <span class="ds-floating-menu__icon-container" aria-hidden="true">
              <ds-icon [name]="item.icon" [size]="'var(--ds-floating-menu-icon-size)'" />
            </span>
            <span class="ds-floating-menu__label ds-text--visual-padding-none">
              {{ item.label }}
            </span>
            @if (item.tag) {
              <span class="ds-floating-menu__tag" aria-hidden="true">
                <ds-tag-pill
                  [label]="item.tag.label"
                  [variant]="item.tag.variant || 'new'"
                  [size]="'xs'"
                  [invert]="item.tag.invert || false"
                />
              </span>
            }
          </button>
        }
      </nav>
    }
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      max-width: 100%;
    }

    .ds-floating-menu {
      display: flex;
      width: calc(
        100% -
        var(--ds-floating-menu-more-viewport-margin) -
        var(--ds-floating-menu-more-viewport-margin)
      );
      height: var(--ds-floating-menu-height);
      align-items: center;
      justify-content: center;
      overflow: hidden;
      padding: 0 var(--ds-floating-menu-padding-x);
      border: var(--ds-floating-menu-border-width) solid var(--ds-floating-menu-border-color);
      border-radius: var(--ds-floating-menu-container-radius);
      background: var(--ds-floating-menu-background);
      box-shadow: var(--ds-floating-menu-shadow);
      box-sizing: border-box;
      margin-inline: var(--ds-floating-menu-more-viewport-margin);
    }

    .ds-floating-menu--more-open {
      position: fixed;
      z-index: 1001;
      right: var(--ds-floating-menu-more-viewport-margin);
      bottom: var(--ds-floating-menu-more-menu-bottom-inset);
      left: var(--ds-floating-menu-more-viewport-margin);
      width: auto;
      margin: auto;
    }

    .ds-floating-menu__item {
      position: relative;
      display: flex;
      flex: 1 1 0;
      flex-direction: column;
      min-width: 0;
      height: 100%;
      align-items: center;
      justify-content: flex-start;
      gap: var(--ds-floating-menu-item-gap);
      padding: var(--ds-floating-menu-item-padding-y) 0;
      border: 0;
      background: transparent;
      box-sizing: border-box;
      color: var(--ds-floating-menu-content-color);
      cursor: pointer;
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-floating-menu-font-size);
      font-weight: var(--ds-floating-menu-font-weight);
      line-height: var(--ds-floating-menu-line-height);
      outline: none;
      transition:
        color var(--ds-floating-menu-transition-duration) ease,
        opacity var(--ds-floating-menu-transition-duration) ease;
    }

    .ds-floating-menu__item::after {
      content: '';
      position: absolute;
      inset: var(--ds-focus-gap);
      border-radius: var(--ds-floating-menu-icon-container-radius);
      box-shadow: inset 0 0 0 var(--ds-focus-stroke) var(--ds-focus-color);
      opacity: 0;
      pointer-events: none;
    }

    .ds-floating-menu__icon-container {
      display: inline-flex;
      width: var(--ds-floating-menu-icon-container-width);
      height: var(--ds-floating-menu-icon-container-height);
      flex: 0 0 auto;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: var(--ds-floating-menu-icon-container-radius);
      background: transparent;
      transition: background-color var(--ds-floating-menu-transition-duration) ease;
    }

    .ds-floating-menu__item:hover:not(:disabled):not(.ds-floating-menu__item--selected)
      .ds-floating-menu__icon-container {
      background: var(--ds-floating-menu-hover-overlay);
    }

    .ds-floating-menu__item--selected {
      color: var(--ds-floating-menu-selected-content-color);
      font-weight: var(--ds-floating-menu-selected-font-weight);
    }

    .ds-floating-menu__item--selected .ds-floating-menu__icon-container {
      background: var(--ds-floating-menu-selected-background);
    }

    .ds-floating-menu__item:focus-visible::after {
      opacity: 1;
    }

    .ds-floating-menu__item:disabled {
      color: var(--ds-floating-menu-disabled-content-color);
      cursor: not-allowed;
      opacity: calc(var(--ds-floating-menu-disabled-opacity) / 100px);
    }

    .ds-floating-menu__label {
      display: block;
      width: 100%;
      min-width: 0;
      overflow: hidden;
      text-align: center;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-floating-menu__tag {
      position: absolute;
      z-index: 1;
      inset-block-start: var(--ds-floating-menu-tag-inset-top);
      inset-inline-start: var(--ds-floating-menu-tag-inset-start);
      display: inline-flex;
      pointer-events: none;
    }

    .ds-floating-menu__overlay {
      position: fixed;
      z-index: 1000;
      inset: 0;
      background: var(--ds-floating-menu-more-overlay);
      animation: ds-floating-menu-overlay-in var(--ds-floating-menu-more-transition-duration) ease-out;
    }

    .ds-floating-menu__dialog {
      position: absolute;
      right: var(--ds-floating-menu-more-viewport-margin);
      left: var(--ds-floating-menu-more-viewport-margin);
      bottom: calc(
        var(--ds-floating-menu-more-menu-bottom-inset) +
        var(--ds-floating-menu-height) +
        var(--ds-floating-menu-more-dialog-gap)
      );
      display: flex;
      width: auto;
      max-height: min(
        var(--ds-floating-menu-more-dialog-max-height),
        calc(
          100vh -
          var(--ds-floating-menu-more-menu-bottom-inset) -
          var(--ds-floating-menu-height) -
          var(--ds-floating-menu-more-dialog-gap) -
          var(--ds-floating-menu-more-viewport-margin)
        )
      );
      flex-direction: column;
      overflow: hidden;
      border: var(--ds-floating-menu-more-dialog-border-width) solid
        var(--ds-floating-menu-more-dialog-border-color);
      border-radius: var(--ds-floating-menu-more-dialog-radius);
      background: var(--ds-floating-menu-more-dialog-background);
      box-sizing: border-box;
    }

    .ds-floating-menu__dialog-header {
      position: relative;
      z-index: 1;
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      gap: var(--ds-floating-menu-more-header-gap);
      padding:
        var(--ds-floating-menu-more-header-padding-top)
        var(--ds-floating-menu-more-header-padding-x)
        var(--ds-floating-menu-more-header-padding-bottom);
      background: var(--ds-floating-menu-more-dialog-background);
      box-shadow: var(--ds-floating-menu-more-header-shadow);
      box-sizing: border-box;
    }

    .ds-floating-menu__dialog-title {
      flex: 1 1 auto;
      min-width: 0;
      margin: 0;
      overflow: hidden;
      color: var(--ds-floating-menu-more-content-color);
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-floating-menu-more-title-font-size);
      font-weight: var(--ds-floating-menu-more-title-font-weight);
      line-height: var(--ds-floating-menu-more-title-line-height);
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-floating-menu__dialog-body {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      gap: var(--ds-floating-menu-more-option-gap);
      min-height: 0;
      overflow-y: auto;
      padding:
        var(--ds-floating-menu-more-body-padding)
        var(--ds-floating-menu-more-body-padding)
        0;
      box-sizing: border-box;
      overscroll-behavior: contain;
    }

    .ds-floating-menu__dialog-body::after {
      content: '';
      width: 100%;
      min-height: 0;
      flex: 0 1 var(--ds-floating-menu-more-body-padding);
      margin-block-start: calc(-1 * var(--ds-floating-menu-more-option-gap));
      pointer-events: none;
    }

    .ds-floating-menu__option {
      display: flex;
      width: 100%;
      height: var(--ds-floating-menu-more-option-height);
      flex: 0 0 auto;
      align-items: center;
      gap: var(--ds-floating-menu-more-option-content-gap);
      padding: var(--ds-floating-menu-more-option-padding);
      overflow: hidden;
      border: 0;
      border-radius: var(--ds-floating-menu-more-option-radius);
      background: transparent;
      box-sizing: border-box;
      color: var(--ds-floating-menu-more-content-color);
      cursor: pointer;
      font-family: 'Sura Sans', system-ui, sans-serif;
      font-size: var(--ds-floating-menu-more-option-font-size);
      font-weight: var(--ds-floating-menu-more-option-font-weight);
      line-height: var(--ds-floating-menu-more-option-line-height);
      outline: none;
      transition:
        background-color var(--ds-floating-menu-more-transition-duration) ease,
        opacity var(--ds-floating-menu-more-transition-duration) ease;
    }

    .ds-floating-menu__option:hover:not(:disabled):not(.ds-floating-menu__option--selected) {
      background: var(--ds-floating-menu-more-option-hover-background);
    }

    .ds-floating-menu__option--selected {
      background: var(--ds-floating-menu-more-option-selected-background);
      color: var(--ds-floating-menu-more-option-selected-content-color);
      font-weight: var(--ds-floating-menu-more-option-selected-font-weight);
    }

    .ds-floating-menu__option:focus-visible {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: calc(-1 * var(--ds-focus-stroke));
    }

    .ds-floating-menu__option:disabled {
      color: var(--ds-floating-menu-more-disabled-content-color);
      cursor: not-allowed;
      opacity: calc(var(--ds-floating-menu-more-disabled-opacity) / 100px);
    }

    .ds-floating-menu__option-label {
      min-width: 0;
      overflow: hidden;
      text-align: left;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ds-text--visual-padding-none {
      text-box-trim: trim-both;
    }

    @keyframes ds-floating-menu-overlay-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @media (prefers-reduced-motion: reduce) {
      .ds-floating-menu__overlay {
        animation: none;
      }

      .ds-floating-menu__item,
      .ds-floating-menu__icon-container,
      .ds-floating-menu__option {
        transition: none;
      }
    }
  `],
})
export class DsFloatingMenuComponent {
  private static dialogId = 0;
  private moreTrigger: HTMLElement | null = null;
  private readonly host: HTMLElement;

  readonly moreDialogTitleId = `ds-floating-menu-more-title-${++DsFloatingMenuComponent.dialogId}`;

  @Input() items: DsFloatingMenuItem[] = [];
  @Input() value = '';
  @Input() disabled = false;
  @Input() ariaLabel = 'Navegación principal';
  @Input() moreItems: DsFloatingMenuOption[] = [];
  @Input() moreValue = '';
  @Input() moreTitle = 'Más opciones';
  @Input() moreOpen = false;
  @Input() dismissOnBackdropClick = true;
  @Input() dismissOnMoreItemSelect = true;
  @Input() closeAriaLabel = 'Cerrar';

  @Output() valueChange = new EventEmitter<string>();
  @Output() itemSelected = new EventEmitter<DsFloatingMenuItem>();
  @Output() moreOpenChange = new EventEmitter<boolean>();
  @Output() moreValueChange = new EventEmitter<string>();
  @Output() moreItemSelected = new EventEmitter<DsFloatingMenuOption>();

  constructor(elementRef: ElementRef<HTMLElement>) {
    this.host = elementRef.nativeElement;
  }

  get selectedIndex(): number {
    const index = this.items.findIndex(item => item.value === this.value);
    return Math.max(index, 0);
  }

  selectAt(
    index: number,
    openMoreMenu = true,
    trigger: EventTarget | null = null,
  ): void {
    const item = this.items[index];
    if (!item || this.disabled || item.disabled) return;

    this.value = item.value;
    this.valueChange.emit(item.value);
    this.itemSelected.emit(item);

    if (openMoreMenu && item.opensMoreMenu && this.moreItems.length > 0) {
      this.openMoreMenu(trigger);
    } else if (this.moreOpen && !item.opensMoreMenu) {
      this.closeMoreMenu(false);
    }

    if (!item.opensMoreMenu && this.moreValue) {
      this.moreValue = '';
      this.moreValueChange.emit('');
    }
  }

  openMoreMenu(trigger: EventTarget | null = null): void {
    if (this.disabled || this.moreItems.length === 0) return;

    this.moreTrigger = trigger instanceof HTMLElement ? trigger : this.moreTrigger;
    this.moreOpen = true;
    this.moreOpenChange.emit(true);
    window.setTimeout(() => {
      this.host
        .querySelector<HTMLElement>('[data-ds-floating-menu-close] button')
        ?.focus();
    }, 0);
  }

  closeMoreMenu(restoreFocus = true): void {
    if (!this.moreOpen) return;

    this.moreOpen = false;
    this.moreOpenChange.emit(false);
    if (restoreFocus) {
      const trigger = this.moreTrigger;
      window.queueMicrotask(() => trigger?.focus());
    }
  }

  selectMoreOption(option: DsFloatingMenuOption): void {
    if (this.disabled || option.disabled) return;

    this.moreValue = option.value;
    this.moreValueChange.emit(option.value);
    this.moreItemSelected.emit(option);
    if (this.dismissOnMoreItemSelect) this.closeMoreMenu();
  }

  isMoreOptionSelected(option: DsFloatingMenuOption): boolean {
    return !this.disabled && !option.disabled && option.value === this.moreValue;
  }

  onBackdropClick(): void {
    if (this.dismissOnBackdropClick) this.closeMoreMenu();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (!this.moreOpen) return;
    event.preventDefault();
    this.closeMoreMenu();
  }

  onDialogKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;

    const focusable = this.dialogFocusableElements();
    if (focusable.length === 0) return;

    const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
    const nextIndex = event.shiftKey
      ? (currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1)
      : (currentIndex === focusable.length - 1 ? 0 : currentIndex + 1);

    event.preventDefault();
    focusable[nextIndex]?.focus();
  }

  onMoreOptionKeydown(event: KeyboardEvent, index: number): void {
    const direction = {
      ArrowDown: 1,
      ArrowUp: -1,
      Home: 'first',
      End: 'last',
    }[event.key] as number | 'first' | 'last' | undefined;

    if (direction === undefined) return;

    event.preventDefault();
    const nextIndex = this.resolveNextMoreIndex(index, direction);
    if (nextIndex === -1) return;

    this.host
      .querySelectorAll<HTMLButtonElement>('.ds-floating-menu__option')
      [nextIndex]
      ?.focus();
  }

  onKeydown(event: KeyboardEvent, index: number): void {
    const direction = {
      ArrowRight: 1,
      ArrowLeft: -1,
      Home: 'first',
      End: 'last',
    }[event.key] as number | 'first' | 'last' | undefined;

    if (direction === undefined) return;

    event.preventDefault();
    const nextIndex = this.resolveNextIndex(index, direction);
    if (nextIndex === -1) return;

    this.selectAt(nextIndex, false);
    this.focusItem(event.currentTarget, nextIndex);
  }

  private resolveNextIndex(
    currentIndex: number,
    direction: number | 'first' | 'last',
  ): number {
    if (direction === 'first') return this.items.findIndex(item => !item.disabled);
    if (direction === 'last') {
      for (let index = this.items.length - 1; index >= 0; index -= 1) {
        if (!this.items[index]?.disabled) return index;
      }
      return -1;
    }

    for (let step = 1; step <= this.items.length; step += 1) {
      const nextIndex = (currentIndex + direction * step + this.items.length) % this.items.length;
      if (!this.items[nextIndex]?.disabled) return nextIndex;
    }
    return -1;
  }

  private resolveNextMoreIndex(
    currentIndex: number,
    direction: number | 'first' | 'last',
  ): number {
    if (direction === 'first') return this.moreItems.findIndex(item => !item.disabled);
    if (direction === 'last') {
      for (let index = this.moreItems.length - 1; index >= 0; index -= 1) {
        if (!this.moreItems[index]?.disabled) return index;
      }
      return -1;
    }

    for (let step = 1; step <= this.moreItems.length; step += 1) {
      const nextIndex =
        (currentIndex + direction * step + this.moreItems.length) % this.moreItems.length;
      if (!this.moreItems[nextIndex]?.disabled) return nextIndex;
    }
    return -1;
  }

  private dialogFocusableElements(): HTMLElement[] {
    return Array.from(
      this.host.querySelectorAll<HTMLElement>(
        '.ds-floating-menu__dialog button:not(:disabled)',
      ),
    );
  }

  private focusItem(currentTarget: EventTarget | null, index: number): void {
    const nav = currentTarget instanceof HTMLElement ? currentTarget.parentElement : null;
    const button = nav?.querySelectorAll<HTMLButtonElement>('.ds-floating-menu__item')[index];
    window.queueMicrotask(() => button?.focus());
  }
}
